import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, addDoc, query, where, getDocs, Timestamp, doc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage'; // Add deleteObject and ref
import { app, db, storage } from '../../firebase'; // Add storage
import { v4 as uuidv4 } from 'uuid';
import ErrorBanner from '../ErrorBanner';
import Modal from '../Modal';
import BikeForm from '../MaintenanceLists/BikeForm';
import BadgeGroup from '../BadgeGroup';
import './MenuMaintenanceTrackers.css';
import { analytics } from '../../firebase';
import { logEvent } from 'firebase/analytics';
import FormattedDate from '../FormattedDate';
import { Alert, Row, Col, ListGroup, Card, Badge, Button, Spinner } from 'react-bootstrap';


const getBrandColors = (make) => {
  const brandColors = {
    'KTM': '#FF6600',
    'HONDA': '#CC0000',
    'BMW': '#0066B1',
    'SUZUKI': '#00406A',
    'YAMAHA': '#003399',
    'KAWASAKI': '#00A44F',
    'MOTO GUZZI': '#C80000',
    'HARLEY-DAVIDSON': '#F47920',
    'HARLEY DAVIDSON': '#F47920',
    'HD': '#F47920',
    'DUCATI': '#BF0000',
    'TRIUMPH': '#5C84B1',
    'APRILIA': '#D70D30',
    'INDIAN': '#B41E3C',
    'ROYAL ENFIELD': '#6A0000',
    'HUSQVARNA': '#0C50A3',
    'MV AGUSTA': '#CF0A12',
    'MV': '#CF0A12',
    'BENELLI': '#006338',
    'BUELL': '#FF6600',
    'BIMOTA': '#E32D22',
    'NORTON': '#C0C0C0',
    'BAJAJ': '#004A8F',
    'TVS': '#E31E24',
    'ZERO': '#6CC049'
  };

  const normalisedMake = make?.toUpperCase();
  return brandColors[normalisedMake] || null;
};

const isLightColor = (hexColor) => {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Calculate perceived brightness (YIQ equation)
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return yiq >= 128;
};

const MaintenanceTracker = ({ user }) => {
  const [bikes, setBikes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [isAddBikeModalOpen, setIsAddBikeModalOpen] = useState(false);
  const [newBike, setNewBike] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    nickname: '',
    purchaseDate: '',
    purchasePrice: '',
    initialMileage: '',
    photo: ''
  });
  const navigate = useNavigate();

  // Effect to fetch bikes
  useEffect(() => {
    const fetchBikes = async () => {
      if (!user) return;
      setLoading(true); // Set loading to true when starting fetch
      try {
        const q = query(collection(db, "bikes"), where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);
        const bikesData = [];
        querySnapshot.forEach((doc) => {
          bikesData.push({ id: doc.id, ...doc.data() });
        });
        setBikes(bikesData);
      } catch (err) {
        console.error("Error fetching bikes:", err);
        setError("Failed to load your bikes. Please refresh the page to try again.");
      } finally {
        setLoading(false); // Set loading to false after fetch
      }
    };
    fetchBikes();
  }, [user]);

  /**
   * Transform Firebase Storage URL to use the thumb version
   * 
   * @param {string} url - Original Firebase Storage URL (may be medium or original)
   * @returns {string} URL with _resized_thumb added before the file extension
   */
  const getThumbImageUrl = (url) => {
    if (!url) return url;
    
    // Check if URL is a Firebase Storage URL
    if (!url.includes('firebasestorage.googleapis.com')) return url;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return url;
    
    const encodedFilename = filenameMatch[1]; // This is URL encoded
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any existing resize suffix (in case it's already a medium version)
    const baseFilename = decodedFilename.replace(/_resized_(medium|thumb)/g, '');
    
    // Extract file extension and directory
    const lastDotIndex = baseFilename.lastIndexOf('.');
    if (lastDotIndex === -1) return url; // No extension found
    
    const lastSlashIndex = baseFilename.lastIndexOf('/');
    const directory = lastSlashIndex !== -1 ? baseFilename.substring(0, lastSlashIndex + 1) : '';
    const filename = lastSlashIndex !== -1 ? baseFilename.substring(lastSlashIndex + 1) : baseFilename;
    
    const filenameWithoutExt = filename.substring(0, filename.lastIndexOf('.'));
    const extension = filename.substring(filename.lastIndexOf('.'));
    
    // Create new filename with _resized_thumb suffix
    const newFilename = `${directory}${filenameWithoutExt}_resized_thumb${extension}`;
    const encodedNewFilename = encodeURIComponent(newFilename);
    
    // Replace the filename in the URL
    return url.replace(encodedFilename, encodedNewFilename);
  };

  /**
   * Extract the base filename from a Firebase Storage URL
   * 
   * @param {string} url - Firebase Storage URL
   * @returns {string|null} Base filename without resize suffix or null if can't parse
   */
  const getBaseFilenameFromUrl = (url) => {
    if (!url) return null;
    
    // Extract the filename from the URL
    const filenameMatch = url.match(/o\/([^?]+)/);
    if (!filenameMatch || filenameMatch.length < 2) return null;
    
    const encodedFilename = filenameMatch[1];
    const decodedFilename = decodeURIComponent(encodedFilename);
    
    // Remove any resize suffix from the filename
    return decodedFilename.replace(/_resized_(medium|thumb)/g, '');
  };

  /**
   * Delete image files from storage
   * 
   * @param {string} url - URL of the image to delete
   * @returns {Promise<void>}
   */
  const deleteImages = async (url) => {
    if (!url) return;
    try {
      const baseFilename = getBaseFilenameFromUrl(url);
      if (!baseFilename) {
        console.log('Error: Could not extract base filename from URL:', url);
        return;
      }
      console.log('Attempting to delete images with base filename:', baseFilename);
      const fileDir = baseFilename.includes('/') ? baseFilename.substring(0, baseFilename.lastIndexOf('/')) : '';
      const fileName = baseFilename.includes('/') ? baseFilename.substring(baseFilename.lastIndexOf('/') + 1) : baseFilename;
      const fileExt = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')) : '';
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      
      // Create references to both resized versions
      const mediumRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_medium${fileExt}`);
      const thumbRef = ref(storage, `${fileDir}/${nameWithoutExt}_resized_thumb${fileExt}`);
      
      // Delete both files
      await Promise.all([
        deleteObject(mediumRef).catch(err => console.log('Medium file may not exist:', err)),
        deleteObject(thumbRef).catch(err => console.log('Thumb file may not exist:', err))
      ]);
      console.log('Successfully deleted image files');
    } catch (error) {
      console.error('Error deleting images:', error);
      // Continue with bike deletion even if image deletion fails
    }
  };

  const createNewBike = async () => {
    try {
      const publicId = uuidv4();
      const now = Timestamp.now();
      
      const bikeData = {
        make: newBike.make,
        model: newBike.model,
        year: newBike.year,
        nickname: newBike.nickname || `${newBike.year} ${newBike.make} ${newBike.model}`,
        purchaseDate: newBike.purchaseDate ? Timestamp.fromDate(new Date(newBike.purchaseDate)) : null,
        purchasePrice: newBike.purchasePrice || null,
        initialMileage: newBike.initialMileage ? parseInt(newBike.initialMileage) : null,
        photo: newBike.photo || null,
        created: now,
        lastEdited: now,
        publicId: publicId,
        userId: user.uid,
        ownerName: user.displayName,
        ownerEmail: user.email,
        ownerPhotoURL: user.photoURL || null,
        maintenanceItems: []
      };
      
      const docRef = await addDoc(collection(db, "bikes"), bikeData);
      logEvent(analytics, 'create_bike', {
        user_id: user.uid,
        timestamp: new Date().toString()
      });
      
      setNewBike({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        nickname: '',
        purchaseDate: '',
        purchasePrice: '',
        initialMileage: '',
        photo: ''
      });
      setIsAddBikeModalOpen(false);
      setBikes([...bikes, { id: docRef.id, ...bikeData }]);
    } catch (error) {
      console.error("Error creating bike:", error);
      setError("Failed to create a new bike. Please try again.");
    }
  };

  const deleteBike = async (bikeId) => {
    try {
      // Find the bike to get its photo URL before deleting
      const bikeToDelete = bikes.find(bike => bike.id === bikeId);
      
      // Delete the document from Firestore
      await deleteDoc(doc(db, "bikes", bikeId));
      
      // Delete associated images if they exist
      if (bikeToDelete && bikeToDelete.photo) {
        await deleteImages(bikeToDelete.photo);
      }
      
      // Update local state
      setBikes(bikes.filter(bike => bike.id !== bikeId));
      
      // Log analytics event
      logEvent(analytics, 'delete_bike', {
        user_id: user.uid,
        timestamp: new Date().toString()
      });
    } catch (error) {
      console.error("Error deleting bike:", error);
      setError("Failed to delete the bike. Please try again.");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleDeleteClick = (e, bikeId) => {
    e.preventDefault();
    e.stopPropagation();
    setConfirmDeleteId(bikeId);
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const handleAddBike = async (e) => {
    e.preventDefault();
    await createNewBike();
  };

  const renderAddBikeModal = () => {
    return (
      <Modal
        isOpen={isAddBikeModalOpen}
        onClose={() => setIsAddBikeModalOpen(false)}
        title="Add New Motorcycle"
      >
        <div className="add-bike-modal">
          <BikeForm
            bike={newBike}
            setBike={setNewBike}
            onSubmit={handleAddBike}
            onCancel={() => setIsAddBikeModalOpen(false)}
            submitButtonText="Add Motorcycle"
            isEdit={false}
          />
        </div>
      </Modal>
    );
  };

  return (
    <div className="maintenance-tracker-container">
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      <Button 
        variant="primary"
        onClick={() => setIsAddBikeModalOpen(true)}
        className="btn-primary-centered">
        Add New Motorcycle
      </Button>
      
      
      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading your motorcycles...</p>
        </div>
      ) : bikes.length > 0 ? (
        <div className="items-container">
          <Row className="g-4">
            {bikes.map(bike => (
              <Col key={bike.id} xs={12} md={12} lg={12} xl={12}>
                <Card className="item-panel h-100">
                  <Link to={`/maintenance/${bike.publicId}`} className="text-decoration-none h-100 d-flex flex-column">
                    <Card.Header 
                      className="item-panel-header"
                      style={{
                        backgroundColor: getBrandColors(bike.make) || 'var(--bs-light)',
                        color: getBrandColors(bike.make) 
                          ? (isLightColor(getBrandColors(bike.make)) ? '#000000' : '#FFFFFF')
                          : 'inherit'
                      }}
                    >
                      <h3 
                        className="heading-small mb-0"
                        style={{
                          color: getBrandColors(bike.make) 
                            ? (isLightColor(getBrandColors(bike.make)) ? '#000000' : '#FFFFFF')
                            : 'var(--moto-brand-accent)'
                        }}
                      >
                        {bike.nickname || `${bike.model}`}
                      </h3>
                    </Card.Header>
                    
                    <Card.Body className="item-panel-content flex-grow-1 position-relative pt-1 pb-0 mb-0">
                      <div className="d-flex align-items-start">
                        {bike.photo && (
                          <div className="item-panel-media" >
                            <img 
                              src={getThumbImageUrl(bike.photo)}
                              alt={bike.nickname || `${bike.year} ${bike.make} ${bike.model}`} 
                            />
                          </div>
                        )}
                        
                        <div className="item-panel-body ms-2 mb-3">
                          <div className="item-panel-details">
                            <Badge bg="primary">{bike.year} {bike.make} {bike.model}</Badge>
  
                            {bike.purchaseDate && (
                              <BadgeGroup
                                label="Purchased"
                                value={<FormattedDate date={bike.purchaseDate} />}
                                color="secondary"
                              />
                            )}
                            
                            {bike.maintenanceItems && (
                              <BadgeGroup 
                                label="Records"
                                value={bike.maintenanceItems.length} 
                                color="success"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                    
                    <Card.Footer className="item-panel-footer mt-auto">
                      <Button 
                        variant="danger" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteClick(e, bike.id);
                        }}
                        size="sm"
                      >
                        Delete
                      </Button>
                    </Card.Footer>
                  </Link>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      ) : (
        <Alert variant="info" className="text-center p-4">
          <p className="mb-0">You haven't added any motorcycles yet. Add your first one to get started!</p>
        </Alert>
      )}
      
      {renderAddBikeModal()}
      
      <Modal
        isOpen={confirmDeleteId !== null}
        onClose={cancelDelete}
        title="Confirm Delete"
      >
        <div className="delete-confirmation">
          <p>Are you sure you want to delete this motorcycle? This action cannot be undone and all maintenance records for this bike will be lost.</p>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button variant="secondary" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => deleteBike(confirmDeleteId)}>
              Delete Motorcycle
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MaintenanceTracker;
